import { useMemo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { RECURRING_TYPES } from 'constants/oneOnOne';
import CustomFormDrawer from '../customFormDrawer';
import { PARAMS } from '../../../constants/pages';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { API_PLANS_BASIC } from '../../../constants/apiRoutes';
import { useAvailableUsers } from '../../../utility/hooks';
import http from '../../../utility/http';
import { hasScopedAuthPermissionCheck } from '../../../utility/permissions';
import {
  getDialogTitle,
  prepareRecordData,
  getNotifyAll,
  getFields,
  prepareDataForSave,
} from './config';

const useStyles = makeStyles(() => ({
  form: {
    display: 'grid',
    gridColumnGap: 24,
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gridTemplateAreas: `
      "status status"
      "user ."
      "date time"
      "recurring tags"
      "agenda agenda"
      "plans ."
      "note note"
      "impression impression"
    `,
  },
  user: {
    gridArea: 'user',
  },
  status: {
    gridArea: 'status',
  },
  date: {
    gridArea: 'date',
  },
  time: {
    gridArea: 'time',
  },
  agenda: {
    gridArea: 'agenda',
  },
  recurring: {
    gridArea: 'recurring',
  },
  tags: {
    gridArea: 'tags',
  },
  plans: {
    gridArea: 'plans',
  },
  privateNote: {
    gridArea: 'note',
  },
  meetingImpression: {
    gridArea: 'impression',
  },
  editorLabelWrapper: {
    justifyContent: 'space-between',
  },
  editorLabel: {
    fontFamily: 'ProximaNova-Bold',
    fontSize: 20,
    lineHeight: '24px',
  },
}));

const ManageOneOnOne = ({
  translations,
  isOpened,
  isReschedule = false,
  newRecurring = false,
  isOneOnOneRequest = false,
  record = null,
  categories,
  topics,
  createdFor = null,
  auth,
  accessControl,
  onCreateTag,
  onDelete = () => {},
  onClose,
  onSave,
}) => {
  const classes = useStyles();
  const [userPlans, setUserPlans] = useState([]);

  const canDelete = hasScopedAuthPermissionCheck(
    PERMISSION_CODES.ONE_ON_ONE_DELETE,
    accessControl,
    auth?.id,
    record?.user?.id
  );
  const canManagePlansCheck = useCallback(
    userId => {
      return hasScopedAuthPermissionCheck(
        PERMISSION_CODES.PLANS_EDIT,
        accessControl,
        auth?.id,
        userId
      );
    },
    [auth?.id, accessControl]
  );

  const isCreatedForCurrentUser =
    createdFor?.id === auth?.id || record?.user?.id === auth?.id;
  const isCreatedByCurrentUser = record?.moderator?.id === auth?.id;

  const params = useMemo(() => {
    return isOpened && !record
      ? {
          [PARAMS.RBAC_SCOPE_PERMISSION]: PERMISSION_CODES.ONE_ON_ONE_EDIT,
          [PARAMS.EXCLUDE]: auth.id,
        }
      : {};
  }, [isOpened, record, auth.id]);
  const allUsers = useAvailableUsers(params, null, true);

  const getUserPlans = useCallback(
    async userId => {
      if (canManagePlansCheck(userId)) {
        const { data } = await http.get(API_PLANS_BASIC, {
          params: { user: userId },
        });

        return setUserPlans(data);
      }
    },
    [canManagePlansCheck]
  );

  useEffect(() => {
    if (isOpened && (createdFor || record)) {
      getUserPlans(createdFor?.id || record?.user?.id);
    }

    return setUserPlans([]);
  }, [isOpened, record, createdFor, getUserPlans]);

  const handleSave = values =>
    onSave(prepareDataForSave(values, record, newRecurring));

  return (
    <CustomFormDrawer
      customFormClass={classes.form}
      translations={translations}
      titleText={getDialogTitle(
        translations.title,
        record?.id,
        isOneOnOneRequest,
        isReschedule
      )}
      isOpened={isOpened}
      isInitialValid={
        !!record?.id && !isOneOnOneRequest && !isReschedule && !newRecurring
      }
      initialData={prepareRecordData(
        record,
        createdFor,
        isOneOnOneRequest,
        isReschedule,
        newRecurring
      )}
      fields={getFields(
        classes,
        translations.formLabels,
        record,
        topics,
        createdFor,
        canManagePlansCheck,
        onCreateTag,
        getUserPlans,
        isReschedule,
        newRecurring,
        isCreatedForCurrentUser,
        isCreatedByCurrentUser
      )}
      userPlans={userPlans}
      allUsers={allUsers}
      categories={categories}
      recurring={RECURRING_TYPES}
      notifyAll={getNotifyAll(createdFor)}
      hideDelete={!canDelete}
      onDelete={onDelete}
      onClose={onClose}
      onSave={handleSave}
      isMiddleWidth
    />
  );
};

ManageOneOnOne.propTypes = {
  translations: PropTypes.object.isRequired,
  isOpened: PropTypes.bool.isRequired,
  topics: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  record: PropTypes.shape({}),
  categories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  createdFor: PropTypes.shape({}),
  auth: PropTypes.shape({}).isRequired,
  accessControl: PropTypes.shape({}).isRequired,
  isOneOnOneRequest: PropTypes.bool,
  isReschedule: PropTypes.bool,
  newRecurring: PropTypes.bool,
  onCreateTag: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default ManageOneOnOne;
